import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, Sparkles, CheckCircle2, XCircle, AlertTriangle, FileText } from 'lucide-react';
import {
  wbAiJobApi,
  type WbAiJobEvent,
  type WbAiJobSubscription,
} from '@/lib/api/services/websiteBuilder';
import { useToast } from '@/hooks/use-toast';

/**
 * Multi-Page AI Generation Modal — the user-facing entry point for Pattern A
 * (BullMQ async job + plan-then-execute + tool-per-file).
 *
 * Lifecycle states:
 *   - 'idle'        : prompt input + Generate button
 *   - 'starting'    : POSTing to /wb/ai-generate-job, waiting for jobId
 *   - 'streaming'   : SSE subscription active, accumulating progress events
 *   - 'success'     : completed phase received, listing created pages + CTA
 *   - 'failed'      : failed phase OR transport error, retry button shown
 *   - 'refused'     : planner refused (too many pages / no pages / etc.)
 *   - 'cancelled'   : user clicked Cancel; pages already created have been
 *                     rolled back by the backend
 *
 * Reconnection: if the SSE stream dies mid-job (network blip), the EventSource
 * + fetch-based subscription will surface an error. The user clicks Retry,
 * which RE-SUBSCRIBES to the SAME jobId (the BullMQ job keeps running in the
 * worker). Backend's stream endpoint replays the last `job.progress` so the
 * UI lands on the correct phase. This is why we keep jobId in state across
 * status transitions instead of clearing it on error.
 *
 * Multi-tenant safety: nothing tenant-sensitive lives in the UI — the
 * backend enforces org/site ownership on every endpoint. The modal just
 * speaks JSON over SSE.
 */
export interface MultiPageGenerateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Site we're generating into. Multi-page generation appends to this site's existing pages. */
  siteId: string;
  /** Human-readable site name shown in the header. Optional — falls back to "site". */
  siteName?: string;
  /**
   * Called when the job completes successfully. Receives the terminal payload
   * so the parent can navigate to the editor for the first page, refresh the
   * pages list, etc. The modal does NOT auto-navigate — that's the parent's
   * decision.
   */
  onComplete?: (result: {
    siteId: string;
    pageCount: number;
    pageSlugs: string[];
    firstPageId: string | null;
    firstPageSlug: string | null;
    datastoreCreated: boolean;
  }) => void;
}

type ViewState =
  | { status: 'idle' }
  | { status: 'starting' }
  | { status: 'streaming'; jobId: string }
  | { status: 'success'; jobId: string; result: SuccessResult }
  | { status: 'failed'; jobId: string | null; message: string; canRetry: boolean }
  | { status: 'refused'; jobId: string; reason: string; message: string }
  | { status: 'cancelled'; jobId: string };

interface SuccessResult {
  siteId: string;
  pageCount: number;
  pageSlugs: string[];
  firstPageId: string | null;
  firstPageSlug: string | null;
  datastoreCreated: boolean;
}

interface PageProgress {
  slug: string;
  title?: string;
  role?: string;
  status: 'pending' | 'generating' | 'saved';
  pageId?: string;
  isHome?: boolean;
}

const EXAMPLE_PROMPTS = [
  'Create a landing page, login page, and signup page for my business. Let users navigate between them and store their account details.',
  'Build a 3-page site: home with hero, about us, and contact form.',
  'Generate a product showcase landing page plus a pricing page with three tiers.',
];

export function MultiPageGenerateModal({
  open,
  onOpenChange,
  siteId,
  siteName,
  onComplete,
}: MultiPageGenerateModalProps) {
  const { toast } = useToast();
  const [prompt, setPrompt] = useState('');
  const [view, setView] = useState<ViewState>({ status: 'idle' });
  const [currentPhase, setCurrentPhase] = useState<string>('');
  const [pages, setPages] = useState<PageProgress[]>([]);
  const [datastoreInfo, setDatastoreInfo] = useState<{ kind?: string; name?: string } | null>(null);
  const subRef = useRef<WbAiJobSubscription | null>(null);

  // Reset everything when the modal closes (so reopen doesn't show stale state).
  useEffect(() => {
    if (!open) {
      subRef.current?.close();
      subRef.current = null;
      // Defer state reset by one tick so the closing animation isn't jarring.
      const t = setTimeout(() => {
        setPrompt('');
        setView({ status: 'idle' });
        setCurrentPhase('');
        setPages([]);
        setDatastoreInfo(null);
      }, 200);
      return () => clearTimeout(t);
    }
    // Cleanup the subscription if the modal stays mounted but unmounts during
    // navigation — common React pattern.
    return () => {
      subRef.current?.close();
      subRef.current = null;
    };
  }, [open]);

  function handleEvent(event: WbAiJobEvent) {
    setCurrentPhase(event.phase);

    switch (event.phase) {
      case 'planned': {
        // Pre-populate the page list with the planner's output so the user
        // sees what's coming. Each entry starts in 'pending' until its
        // page_generating event arrives.
        if (Array.isArray(event.pages)) {
          setPages(
            event.pages.map((p) => ({
              slug: p.slug,
              title: p.title,
              role: p.role,
              status: 'pending',
              isHome: !!p.is_home,
            })),
          );
        }
        break;
      }
      case 'datastore_setup':
        setDatastoreInfo({ kind: event.kind, name: event.name });
        break;
      case 'page_generating': {
        setPages((prev) => {
          const idx = prev.findIndex((p) => p.slug === event.page_slug);
          if (idx === -1) {
            // Planner phase missing? Insert at the right index.
            return [
              ...prev,
              {
                slug: event.page_slug,
                title: event.page_title,
                role: event.page_role,
                status: 'generating',
              },
            ];
          }
          const next = [...prev];
          next[idx] = { ...next[idx], status: 'generating' };
          return next;
        });
        break;
      }
      case 'page_saved': {
        setPages((prev) => {
          const idx = prev.findIndex((p) => p.slug === event.page_slug);
          if (idx === -1) {
            return [
              ...prev,
              {
                slug: event.page_slug,
                status: 'saved',
                pageId: event.page_id,
                isHome: !!event.is_home,
              },
            ];
          }
          const next = [...prev];
          next[idx] = { ...next[idx], status: 'saved', pageId: event.page_id };
          return next;
        });
        break;
      }
      case 'completed': {
        const result: SuccessResult = {
          siteId: event.site_id,
          pageCount: event.page_count,
          pageSlugs: event.page_slugs || [],
          firstPageId: event.first_page_id ?? null,
          firstPageSlug: event.first_page_slug ?? null,
          datastoreCreated: !!event.datastore_created,
        };
        setView((prev) =>
          prev.status === 'streaming'
            ? { status: 'success', jobId: prev.jobId, result }
            : prev,
        );
        // Fire callback exactly once on success.
        onComplete?.(result);
        break;
      }
      case 'failed': {
        const msg = event.message || event.reason || 'Generation failed';
        setView((prev) => ({
          status: 'failed',
          jobId: prev.status === 'streaming' ? prev.jobId : null,
          message: msg,
          canRetry: true,
        }));
        break;
      }
      case 'refused': {
        setView((prev) =>
          prev.status === 'streaming'
            ? {
                status: 'refused',
                jobId: prev.jobId,
                reason: event.reason,
                message: event.message || 'The planner declined this request',
              }
            : prev,
        );
        break;
      }
      case 'cancelled': {
        setView((prev) =>
          prev.status === 'streaming' ? { status: 'cancelled', jobId: prev.jobId } : prev,
        );
        break;
      }
      default:
        break;
    }
  }

  function subscribeJob(jobId: string) {
    subRef.current?.close();
    subRef.current = wbAiJobApi.subscribe(jobId, {
      onEvent: handleEvent,
      onDone: () => {
        subRef.current = null;
        // If we get [DONE] without a terminal phase event (proxy ate it),
        // upgrade `streaming` → `failed` with a "no terminal" message so the
        // UI doesn't get stuck on a spinner. Terminal-phase handlers above
        // run synchronously before onDone, so this only fires on the
        // pathological proxy-cut case.
        setView((prev) =>
          prev.status === 'streaming'
            ? {
                status: 'failed',
                jobId: prev.jobId,
                message: 'Stream ended unexpectedly. Try again.',
                canRetry: true,
              }
            : prev,
        );
      },
      onError: (msg) => {
        subRef.current = null;
        setView((prev) =>
          prev.status === 'streaming' || prev.status === 'starting'
            ? {
                status: 'failed',
                jobId: prev.status === 'streaming' ? prev.jobId : null,
                message: msg,
                canRetry: true,
              }
            : prev,
        );
      },
    });
  }

  async function handleStart() {
    const trimmed = prompt.trim();
    if (!trimmed) {
      toast({ title: 'Prompt required', description: 'Tell the AI what pages you want.', variant: 'destructive' });
      return;
    }
    if (trimmed.length > 4000) {
      toast({ title: 'Prompt too long', description: 'Max 4000 characters.', variant: 'destructive' });
      return;
    }
    setView({ status: 'starting' });
    setPages([]);
    setDatastoreInfo(null);
    setCurrentPhase('starting');

    const res = await wbAiJobApi.start({ siteId, prompt: trimmed });
    if (res.error) {
      setView({ status: 'failed', jobId: null, message: res.error, canRetry: true });
      return;
    }
    const data: any = (res.data as any)?.data ?? res.data;
    const jobId = data?.jobId;
    if (!jobId) {
      setView({ status: 'failed', jobId: null, message: 'Server did not return a job ID', canRetry: true });
      return;
    }
    setView({ status: 'streaming', jobId });
    subscribeJob(jobId);
  }

  async function handleCancel() {
    if (view.status !== 'streaming') return;
    const jobId = view.jobId;
    subRef.current?.close();
    subRef.current = null;
    // Optimistic — mark cancelling state immediately, then ask backend to
    // rollback. If backend cancel fails, we still close the SSE; the worker
    // may still complete in the background but its result won't be applied
    // by the UI.
    setView({ status: 'cancelled', jobId });
    try {
      await wbAiJobApi.cancel(jobId);
    } catch {
      /* best-effort */
    }
  }

  function handleRetryReconnect() {
    if (view.status !== 'failed' || !view.jobId) {
      // No jobId means we never got past starting → restart from scratch.
      handleStart();
      return;
    }
    // Re-subscribe to the same jobId. Backend replays the last phase.
    setView({ status: 'streaming', jobId: view.jobId });
    subscribeJob(view.jobId);
  }

  function handleRetryFromScratch() {
    setView({ status: 'idle' });
    setPages([]);
    setDatastoreInfo(null);
    setCurrentPhase('');
  }

  const isBusy = view.status === 'starting' || view.status === 'streaming';

  return (
    <Dialog open={open} onOpenChange={(o) => {
      // While busy, force the user to either Cancel or wait — closing the
      // modal mid-stream would leave the worker running orphan.
      if (!o && isBusy) {
        toast({
          title: 'Generation in progress',
          description: 'Click Cancel to stop, or wait for it to finish.',
        });
        return;
      }
      onOpenChange(o);
    }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="wb-multi-page-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-500" />
            Generate multiple pages with AI
          </DialogTitle>
          <DialogDescription>
            Describe the pages you want. The AI will plan a site layout, then generate each
            page with working navigation between them.
            {siteName && (
              <span className="block mt-1 text-xs text-muted-foreground">
                Adding to site: <strong>{siteName}</strong>
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* IDLE — prompt input */}
        {view.status === 'idle' && (
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="wb-multi-page-prompt">What pages do you need?</Label>
              <Textarea
                id="wb-multi-page-prompt"
                data-testid="wb-multi-page-prompt"
                rows={4}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g. Create a landing page, a login page, and a signup page for my business."
                className="mt-1"
                maxLength={4000}
              />
              <div className="mt-1 text-xs text-muted-foreground flex justify-between">
                <span>The AI builds up to 10 pages per request.</span>
                <span>{prompt.length}/4000</span>
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">
                Need inspiration?
              </div>
              <div className="flex flex-col gap-1">
                {EXAMPLE_PROMPTS.map((ex) => (
                  <button
                    key={ex}
                    type="button"
                    onClick={() => setPrompt(ex)}
                    className="text-left text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded px-2 py-1 transition"
                  >
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* STARTING / STREAMING — progress UI */}
        {(view.status === 'starting' || view.status === 'streaming') && (
          <div className="space-y-3 py-2" data-testid="wb-multi-page-progress">
            <PhaseStatus phase={currentPhase} datastoreInfo={datastoreInfo} pages={pages} />
            {pages.length > 0 && (
              <div className="space-y-1.5 mt-3">
                <div className="text-xs font-medium text-muted-foreground">Pages</div>
                {pages.map((p) => (
                  <PageRow key={p.slug} page={p} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* SUCCESS */}
        {view.status === 'success' && (
          <div className="space-y-3 py-2" data-testid="wb-multi-page-success">
            <div className="flex items-center gap-2 text-green-600 font-medium">
              <CheckCircle2 className="h-5 w-5" />
              Site generated — {view.result.pageCount} {view.result.pageCount === 1 ? 'page' : 'pages'} created
            </div>
            {view.result.datastoreCreated && (
              <div className="text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1.5">
                A users datastore was created for the auth flow.
              </div>
            )}
            <ul className="text-sm space-y-1">
              {view.result.pageSlugs.map((slug) => (
                <li key={slug} className="flex items-center gap-2">
                  <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                  <code className="text-xs">/{slug}</code>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* FAILED */}
        {view.status === 'failed' && (
          <div className="space-y-3 py-2" data-testid="wb-multi-page-failed">
            <div className="flex items-start gap-2 text-red-600">
              <XCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <div className="font-medium">Could not generate the site</div>
                <div className="text-sm text-red-700/80">{view.message}</div>
              </div>
            </div>
            {view.jobId && (
              <div className="text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1.5">
                The job may still be running in the background. Click "Try to reconnect" to
                pick up where it left off.
              </div>
            )}
          </div>
        )}

        {/* REFUSED — planner returned a refusal (too many pages, no pages, etc.) */}
        {view.status === 'refused' && (
          <div className="space-y-3 py-2" data-testid="wb-multi-page-refused">
            <div className="flex items-start gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <div className="font-medium">The AI couldn't proceed</div>
                <div className="text-sm text-amber-700/80">{view.message}</div>
                <div className="text-xs text-muted-foreground mt-1">Reason code: <code>{view.reason}</code></div>
              </div>
            </div>
          </div>
        )}

        {/* CANCELLED */}
        {view.status === 'cancelled' && (
          <div className="space-y-3 py-2" data-testid="wb-multi-page-cancelled">
            <div className="text-sm text-muted-foreground">
              Generation cancelled. Any pages that were already created have been removed.
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          {view.status === 'idle' && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                onClick={handleStart}
                disabled={!prompt.trim()}
                data-testid="wb-multi-page-start"
              >
                <Sparkles className="h-4 w-4 mr-1" />
                Generate
              </Button>
            </>
          )}
          {view.status === 'starting' && (
            <Button disabled data-testid="wb-multi-page-starting">
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              Starting...
            </Button>
          )}
          {view.status === 'streaming' && (
            <Button
              variant="outline"
              onClick={handleCancel}
              data-testid="wb-multi-page-cancel"
            >
              Cancel
            </Button>
          )}
          {view.status === 'success' && (
            <Button onClick={() => onOpenChange(false)} data-testid="wb-multi-page-done">
              Done
            </Button>
          )}
          {view.status === 'failed' && (
            <>
              <Button variant="outline" onClick={handleRetryFromScratch}>
                Start over
              </Button>
              <Button onClick={handleRetryReconnect} data-testid="wb-multi-page-retry">
                {view.jobId ? 'Try to reconnect' : 'Try again'}
              </Button>
            </>
          )}
          {(view.status === 'refused' || view.status === 'cancelled') && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
              <Button onClick={handleRetryFromScratch} data-testid="wb-multi-page-restart">
                Try a different prompt
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Top-level progress indicator that surfaces the current phase as a human
 * sentence. Edge case: phase `planned` is transient — by the time the user
 * sees it, page_generating has already started — so we render it as
 * "Generating pages...".
 */
function PhaseStatus({
  phase,
  datastoreInfo,
  pages,
}: {
  phase: string;
  datastoreInfo: { kind?: string; name?: string } | null;
  pages: PageProgress[];
}) {
  const message = phaseToMessage(phase, datastoreInfo, pages);
  return (
    <div className="flex items-center gap-2 text-sm font-medium">
      <Loader2 className="h-4 w-4 animate-spin text-purple-500" />
      <span>{message}</span>
    </div>
  );
}

function phaseToMessage(
  phase: string,
  datastoreInfo: { kind?: string; name?: string } | null,
  pages: PageProgress[],
): string {
  switch (phase) {
    case 'queued':
      return 'Queued — waiting for an available worker...';
    case 'planning':
      return 'Planning your site...';
    case 'planned':
      return 'Plan ready. Generating pages...';
    case 'datastore_setup':
      return `Setting up ${datastoreInfo?.kind || 'data'} store${datastoreInfo?.name ? ` (${datastoreInfo.name})` : ''}...`;
    case 'datastore_ready':
      return 'Data store ready. Generating pages...';
    case 'page_generating': {
      const gen = pages.find((p) => p.status === 'generating');
      if (gen) {
        const total = pages.length || 0;
        const idx = pages.findIndex((p) => p.slug === gen.slug) + 1;
        return `Generating ${gen.title || gen.slug}${total ? ` (${idx}/${total})` : ''}...`;
      }
      return 'Generating a page...';
    }
    case 'page_saved':
      return 'Page saved. Moving to the next one...';
    case 'finalizing':
      return 'Finalizing...';
    default:
      return 'Working...';
  }
}

function PageRow({ page }: { page: PageProgress }) {
  return (
    <div
      className="flex items-center justify-between text-sm bg-muted/30 rounded px-2 py-1.5"
      data-testid={`wb-multi-page-row-${page.slug}`}
      data-status={page.status}
    >
      <div className="flex items-center gap-2 min-w-0">
        <FileText className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
        <div className="truncate">
          <span className="font-medium">{page.title || page.slug}</span>
          {page.isHome && <span className="ml-2 text-xs text-purple-600">home</span>}
        </div>
      </div>
      <div className="flex items-center gap-1 text-xs flex-shrink-0 ml-2">
        {page.status === 'pending' && <span className="text-muted-foreground">queued</span>}
        {page.status === 'generating' && (
          <>
            <Loader2 className="h-3 w-3 animate-spin text-purple-500" />
            <span className="text-purple-600">generating</span>
          </>
        )}
        {page.status === 'saved' && (
          <>
            <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
            <span className="text-green-700">done</span>
          </>
        )}
      </div>
    </div>
  );
}
